import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Rating,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  useTheme,
  LinearProgress
} from '@mui/material';
import { MatchData, LineupPlayer } from '@/interfaces/liveGamesType';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { FiUser, FiActivity } from 'react-icons/fi';

interface PlayerAnalysisProps {
  match: MatchData;
  isLoading?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface PlayerStats {
  passes: number;
  passAccuracy: number;
  shots: number;
  tackles: number;
  interceptions: number;
  fouls: number;
  rating: number;
  assists: number;
  distance: number;
  speed: number;
}

interface PlayerWithStats extends LineupPlayer {
  stats: PlayerStats;
}

interface PlayerStat {
  name: string;
  team: 'home' | 'away';
  position: string;
  rating: number;
  goals: number;
  assists: number;
  passes: number;
  passAccuracy: number;
  shots: number;
  tackles: number;
  fouls: number;
  avatar?: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`player-tabpanel-${index}`}
      aria-labelledby={`player-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const PlayerAnalysis: React.FC<PlayerAnalysisProps> = ({ match, isLoading = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [homePlayerSelected, setHomePlayerSelected] = useState<string>('');
  const [awayPlayerSelected, setAwayPlayerSelected] = useState<string>('');
  const theme = useTheme();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleHomePlayerChange = (event: SelectChangeEvent) => {
    setHomePlayerSelected(event.target.value);
  };

  const handleAwayPlayerChange = (event: SelectChangeEvent) => {
    setAwayPlayerSelected(event.target.value);
  };

  // Função para gerar estatísticas simuladas para jogadores
  const generatePlayerStats = (player: LineupPlayer): PlayerStats => {
    // Usar o número da camisa como seed para gerar valores mais consistentes
    const playerNumber = parseInt(player.lineup_number, 10) || Math.floor(Math.random() * 30);
    const isDefender = player.lineup_position?.toLowerCase().includes('defender') || false;
    const isMidfielder = player.lineup_position?.toLowerCase().includes('midfielder') || false;
    const isForward = player.lineup_position?.toLowerCase().includes('forward') || false;

    // Ajustar estatísticas com base na posição
    let passBase = 20;
    let passAccuracyBase = 70;
    let shotsBase = 1;
    let tacklesBase = 2;
    let interceptionsBase = 2;
    let foulsBase = 1;
    let assistsBase = 0;
    let distanceBase = 7;
    let speedBase = 20;

    if (isDefender) {
      tacklesBase += 3;
      interceptionsBase += 2;
      shotsBase -= 0.5;
    } else if (isMidfielder) {
      passBase += 10;
      passAccuracyBase += 5;
      assistsBase += 0.5;
      distanceBase += 1;
    } else if (isForward) {
      shotsBase += 3;
      assistsBase += 1;
      speedBase += 5;
    }

    return {
      passes: Math.floor(Math.random() * 20) + passBase,
      passAccuracy: Math.floor(Math.random() * 15) + passAccuracyBase,
      shots: Math.floor(Math.random() * 3) + shotsBase,
      tackles: Math.floor(Math.random() * 3) + tacklesBase,
      interceptions: Math.floor(Math.random() * 3) + interceptionsBase,
      fouls: Math.floor(Math.random() * 2) + foulsBase,
      rating: Math.min(10, (Math.random() * 2) + 5),
      assists: Math.floor(Math.random() * 2) + assistsBase,
      distance: Math.floor(Math.random() * 3) + distanceBase,
      speed: Math.floor(Math.random() * 10) + speedBase
    };
  };

  // Preparar jogadores com estatísticas
  const homePlayers = useMemo(() => {
    if (!match.lineup?.home?.starting_lineups) return [];
    return match.lineup.home.starting_lineups.map(player => ({
      ...player,
      stats: generatePlayerStats(player)
    }));
  }, [match.lineup?.home?.starting_lineups]);

  const awayPlayers = useMemo(() => {
    if (!match.lineup?.away?.starting_lineups) return [];
    return match.lineup.away.starting_lineups.map(player => ({
      ...player,
      stats: generatePlayerStats(player)
    }));
  }, [match.lineup?.away?.starting_lineups]);

  // Encontrar jogadores selecionados
  const selectedHomePlayer = useMemo(() => {
    return homePlayers.find(player => player.lineup_player === homePlayerSelected);
  }, [homePlayers, homePlayerSelected]);

  const selectedAwayPlayer = useMemo(() => {
    return awayPlayers.find(player => player.lineup_player === awayPlayerSelected);
  }, [awayPlayers, awayPlayerSelected]);

  // Renderizar tabela de jogadores com estatísticas
  const renderPlayerTable = (players: PlayerWithStats[] = []) => {
    if (!players || players.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center">
          Dados de jogadores não disponíveis
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Jogador</TableCell>
              <TableCell align="center">Passes</TableCell>
              <TableCell align="center">Precisão</TableCell>
              <TableCell align="center">Chutes</TableCell>
              <TableCell align="center">Desarmes</TableCell>
              <TableCell align="center">Avaliação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((player, index) => {
              const stats = player.stats;
              return (
                <TableRow key={`${player.lineup_player}-${index}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                        {player.lineup_number}
                      </Avatar>
                      <Typography variant="body2">
                        {player.lineup_player}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">{stats.passes}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${stats.passAccuracy}%`}
                      size="small"
                      color={stats.passAccuracy > 85 ? "success" : stats.passAccuracy > 75 ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">{stats.shots}</TableCell>
                  <TableCell align="center">{stats.tackles}</TableCell>
                  <TableCell align="center">
                    <Rating
                      value={stats.rating}
                      precision={0.5}
                      size="small"
                      readOnly
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Renderizar gráfico de radar para comparação de jogadores
  const renderPlayerComparison = () => {
    if (!selectedHomePlayer || !selectedAwayPlayer) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Selecione um jogador de cada time para comparar
          </Typography>
        </Box>
      );
    }

    // Preparar dados para o gráfico de radar
    const radarData = [
      {
        attribute: 'Passes',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.passes,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.passes,
        fullMark: 50
      },
      {
        attribute: 'Precisão',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.passAccuracy,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.passAccuracy,
        fullMark: 100
      },
      {
        attribute: 'Chutes',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.shots,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.shots,
        fullMark: 10
      },
      {
        attribute: 'Desarmes',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.tackles,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.tackles,
        fullMark: 10
      },
      {
        attribute: 'Interceptações',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.interceptions,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.interceptions,
        fullMark: 10
      },
      {
        attribute: 'Distância (km)',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.distance,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.distance,
        fullMark: 15
      },
      {
        attribute: 'Velocidade (km/h)',
        [selectedHomePlayer.lineup_player]: selectedHomePlayer.stats.speed,
        [selectedAwayPlayer.lineup_player]: selectedAwayPlayer.stats.speed,
        fullMark: 40
      }
    ];

    return (
      <>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="home-player-select-label">{match.match_hometeam_name}</InputLabel>
              <Select
                labelId="home-player-select-label"
                id="home-player-select"
                value={homePlayerSelected}
                label={match.match_hometeam_name}
                onChange={handleHomePlayerChange}
              >
                {homePlayers.map((player) => (
                  <MenuItem key={player.lineup_player} value={player.lineup_player}>
                    {player.lineup_number} - {player.lineup_player}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="away-player-select-label">{match.match_awayteam_name}</InputLabel>
              <Select
                labelId="away-player-select-label"
                id="away-player-select"
                value={awayPlayerSelected}
                label={match.match_awayteam_name}
                onChange={handleAwayPlayerChange}
              >
                {awayPlayers.map((player) => (
                  <MenuItem key={player.lineup_player} value={player.lineup_player}>
                    {player.lineup_number} - {player.lineup_player}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ height: 300, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="attribute" />
              <PolarRadiusAxis angle={30} domain={[0, 'auto']} />
              <Radar
                name={selectedHomePlayer.lineup_player}
                dataKey={selectedHomePlayer.lineup_player}
                stroke="#1976d2"
                fill="#1976d2"
                fillOpacity={0.3}
              />
              <Radar
                name={selectedAwayPlayer.lineup_player}
                dataKey={selectedAwayPlayer.lineup_player}
                stroke="#f44336"
                fill="#f44336"
                fillOpacity={0.3}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </Box>

        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedHomePlayer.lineup_player} ({selectedHomePlayer.lineup_position})
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Passes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedHomePlayer.stats.passes}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Precisão:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedHomePlayer.stats.passAccuracy}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Chutes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedHomePlayer.stats.shots}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Desarmes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedHomePlayer.stats.tackles}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Avaliação:</Typography>
                  <Rating value={selectedHomePlayer.stats.rating} precision={0.5} size="small" readOnly />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedAwayPlayer.lineup_player} ({selectedAwayPlayer.lineup_position})
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Passes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedAwayPlayer.stats.passes}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Precisão:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedAwayPlayer.stats.passAccuracy}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Chutes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedAwayPlayer.stats.shots}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Desarmes:</Typography>
                  <Typography variant="body2" fontWeight="bold">{selectedAwayPlayer.stats.tackles}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Avaliação:</Typography>
                  <Rating value={selectedAwayPlayer.stats.rating} precision={0.5} size="small" readOnly />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  // Verificar se temos dados de escalação
  if (!match.lineup || (!match.lineup.home && !match.lineup.away)) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Análise de Jogadores
        </Typography>
        <Box p={3} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            Dados de jogadores não disponíveis para esta partida
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Dados simulados de jogadores em destaque
  const topPlayers: PlayerStat[] = [
    {
      name: "João Silva",
      team: "home",
      position: "Atacante",
      rating: 8.5,
      goals: 2,
      assists: 1,
      passes: 35,
      passAccuracy: 85,
      shots: 4,
      tackles: 1,
      fouls: 2
    },
    {
      name: "Carlos Oliveira",
      team: "away",
      position: "Meio-campista",
      rating: 7.8,
      goals: 1,
      assists: 0,
      passes: 48,
      passAccuracy: 92,
      shots: 2,
      tackles: 3,
      fouls: 1
    },
    {
      name: "Pedro Santos",
      team: "home",
      position: "Meio-campista",
      rating: 7.5,
      goals: 0,
      assists: 2,
      passes: 52,
      passAccuracy: 88,
      shots: 1,
      tackles: 4,
      fouls: 0
    },
    {
      name: "Lucas Pereira",
      team: "away",
      position: "Atacante",
      rating: 7.2,
      goals: 1,
      assists: 1,
      passes: 28,
      passAccuracy: 75,
      shots: 3,
      tackles: 0,
      fouls: 3
    }
  ];

  // Renderizar cartão de jogador
  const renderPlayerCard = (player: PlayerStat) => {
    const isHomeTeam = player.team === 'home';
    const teamName = isHomeTeam ? match.match_hometeam_name : match.match_awayteam_name;
    const teamColor = isHomeTeam ? theme.palette.primary.main : theme.palette.error.main;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          borderLeft: `4px solid ${teamColor}`
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            src={player.avatar}
            sx={{
              width: 50,
              height: 50,
              mr: 2,
              bgcolor: teamColor
            }}
          >
            {player.name.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">{player.name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {player.position} • {teamName}
            </Typography>
          </Box>
          <Box
            sx={{
              ml: 'auto',
              bgcolor: teamColor,
              color: 'white',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}
          >
            {player.rating.toFixed(1)}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Gols</Typography>
            <Typography variant="body2" fontWeight="medium">{player.goals}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Assistências</Typography>
            <Typography variant="body2" fontWeight="medium">{player.assists}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Passes</Typography>
            <Typography variant="body2" fontWeight="medium">{player.passes}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">Chutes</Typography>
            <Typography variant="body2" fontWeight="medium">{player.shots}</Typography>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="textSecondary">Precisão de passes</Typography>
            <Typography variant="caption" fontWeight="bold">{player.passAccuracy}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={player.passAccuracy}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: teamColor
              }
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FiUser style={{ marginRight: 8 }} /> Análise de Jogadores
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          Jogadores em Destaque
        </Typography>
        <Grid container spacing={2}>
          {topPlayers.map((player, index) => (
            <Grid item xs={12} sm={6} key={`player-${index}`}>
              {renderPlayerCard(player)}
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FiActivity style={{ marginRight: 8 }} /> Estatísticas de Equipe
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              {match.match_hometeam_name}
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2">Posse de bola efetiva</Typography>
              <Typography variant="body2" fontWeight="bold">65%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={65}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: theme.palette.primary.main
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              {match.match_awayteam_name}
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2">Posse de bola efetiva</Typography>
              <Typography variant="body2" fontWeight="bold">35%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={35}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: theme.palette.error.main
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h6" color="primary.main">85%</Typography>
              <Typography variant="caption" color="textSecondary">
                Precisão de passes ({match.match_hometeam_name})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h6" color="error.main">78%</Typography>
              <Typography variant="caption" color="textSecondary">
                Precisão de passes ({match.match_awayteam_name})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h6" color="primary.main">12</Typography>
              <Typography variant="caption" color="textSecondary">
                Dribles bem-sucedidos ({match.match_hometeam_name})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center">
              <Typography variant="h6" color="error.main">8</Typography>
              <Typography variant="caption" color="textSecondary">
                Dribles bem-sucedidos ({match.match_awayteam_name})
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="player analysis tabs"
        >
          <Tab label={match.match_hometeam_name} />
          <Tab label={match.match_awayteam_name} />
          <Tab label="Comparação" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {renderPlayerTable(homePlayers)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderPlayerTable(awayPlayers)}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderPlayerComparison()}
      </TabPanel>
    </Paper>
  );
};

export default PlayerAnalysis;